import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, MapPin, Calendar, Gauge, Clock, Star } from 'lucide-react';
import { Button } from '../ui/button';
import { formatPrice, formatNumber, formatRelativeTime, getConditionBadgeColor, getConditionLabel } from '../../lib/utils';

export const ListingCard = ({ listing, onSave, isSaved = false, showSaveButton = true }) => {
  const primaryImage = listing.images?.[0]?.image_url || 'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?w=400&h=300&fit=crop';
  
  return (
    <div 
      className="listing-card group relative bg-white border border-border overflow-hidden hover:border-primary/50 transition-colors duration-300 rounded-md"
      data-testid={`listing-card-${listing.id}`}
    >
      {/* Image Container */}
      <Link to={`/listings/${listing.id}`} className="block relative aspect-[4/3] overflow-hidden">
        <img
          src={primaryImage}
          alt={`${listing.year} ${listing.make} ${listing.model}`}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        
        {/* Featured Badge */}
        {listing.featured && (
          <div className="absolute top-3 left-3 flex items-center gap-1 bg-accent text-white px-2 py-1 text-xs font-bold uppercase tracking-wider">
            <Star className="h-3 w-3 fill-current" />Destacado</div>
        )}
        
        {/* Condition Badge */}
        <div className={`absolute top-3 right-3 ${getConditionBadgeColor(listing.condition)} text-white px-2 py-1 text-xs font-bold uppercase tracking-wider`}>
          {getConditionLabel(listing.condition)}
        </div>
        
        {/* Save Button - Bottom Right of Image */}
        {showSaveButton && (
          <Button
            variant="ghost"
            size="icon"
            className={`absolute bottom-3 right-3 z-10 bg-white/90 hover:bg-white rounded-full shadow-md ${isSaved ? 'text-red-500' : 'text-muted-foreground'}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onSave?.(listing.id);
            }}
            data-testid={`save-listing-${listing.id}`}
          >
            <Heart className={`h-4 w-4 ${isSaved ? 'fill-current' : ''}`} />
          </Button>
        )}
        
        {/* Overlay on Hover */}
        <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/10 transition-colors duration-300 pointer-events-none" />
      </Link>

      {/* Content */}
      <div className="p-4 space-y-3">
        {/* Title & Price */}
        <div className="flex justify-between items-start gap-2">
          <Link 
            to={`/listings/${listing.id}`}
            className="flex-1"
          >
            <h3 className="font-heading text-lg font-bold uppercase tracking-tight line-clamp-1 hover:text-accent transition-colors">
              {listing.year} {listing.make} {listing.model}
            </h3>
          </Link>
        </div>

        {/* Price */}
        <div className="price-tag">
          {formatPrice(listing.price)}
        </div>

        {/* Quick Stats */}
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
          {listing.mileage && (
            <div className="flex items-center gap-1">
              <Gauge className="h-3.5 w-3.5" />
              <span>{formatNumber(listing.mileage)} km</span>
            </div>
          )}
          {listing.engine_hours && (
            <div className="flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" />
              <span>{formatNumber(listing.engine_hours)} hrs</span>
            </div>
          )}
          <div className="flex items-center gap-1">
            <Calendar className="h-3.5 w-3.5" />
            <span>{listing.year}</span>
          </div>
        </div>

        {/* Location */}
        <div className="flex items-center gap-1 text-sm text-muted-foreground">
          <MapPin className="h-3.5 w-3.5" />
          <span>{listing.location_city}, {listing.location_state}</span>
        </div>

        {/* Seller Info & Time */}
        <div className="flex items-center justify-between pt-2 border-t border-border text-xs text-muted-foreground">
          <span className="uppercase tracking-wide">
            {listing.seller_role === 'dealer' ? listing.seller_company || 'Dealer' : listing.seller_name || 'Private Seller'}
          </span>
          <span>{formatRelativeTime(listing.created_at)}</span>
        </div>
      </div>
    </div>
  );
};

export default ListingCard;
